import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Popover,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import http from '../../services/http';
import CalendarEventDialog from '../CalendarEventDialog';
import { formatEventRange } from '../../utils/calendarUtils';

function rangeAroundNow(daysBack = 7, daysForward = 21) {
  const start = new Date();
  start.setDate(start.getDate() - daysBack);
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setDate(end.getDate() + daysForward);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

const TicketAgendaPanel = ({ ticketId, ticketNumber, theme, users = [], currentUser, canEditTicket }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogReadOnly, setDialogReadOnly] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const load = useCallback(async () => {
    if (!ticketId) return;
    const { start, end } = rangeAroundNow();
    setLoading(true);
    try {
      const res = await http.get(`/ticket/${ticketId}/calendar-events`, {
        params: { from: start.toISOString(), to: end.toISOString() },
      });
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setDialogReadOnly(false);
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setDialogReadOnly(false);
    setDialogOpen(true);
  };

  const openView = (ev) => {
    setEditing(ev);
    setDialogReadOnly(true);
    setDialogOpen(true);
  };

  const isMine = (ev) => Number(ev.user_id) === Number(currentUser?.id);

  // Proximo compromisso: primeiro evento que ainda nao terminou, ordenado por inicio.
  const nowMs = Date.now();
  const nextEv = [...events]
    .filter((ev) => {
      const ref = ev.ends_at || ev.starts_at;
      return ref ? new Date(ref).getTime() >= nowMs : false;
    })
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())[0] || null;

  const compactWhen = (ev) => {
    if (!ev?.starts_at) return '';
    const d = new Date(ev.starts_at);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    if (ev.all_day) return `${dd}/${mm}`;
    const hh = String(d.getHours()).padStart(2, '0');
    const min = d.getMinutes();
    return min === 0 ? `${dd}/${mm} ${hh}h` : `${dd}/${mm} ${hh}:${String(min).padStart(2, '0')}`;
  };

  const popoverOpen = Boolean(anchorEl);

  return (
    <>
      {/* Gatilho minimalista no topo: proximo compromisso (ou "Agenda"). Sem fundo. */}
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        title="Agenda do ticket"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          maxWidth: 240,
          minWidth: 0,
          color: theme.palette.text.secondary,
          '&:hover': { color: theme.palette.text.primary },
        }}
      >
        <EventOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
        <Typography variant="caption" noWrap sx={{ minWidth: 0, lineHeight: 1.2 }}>
          {nextEv ? `${nextEv.title} · ${compactWhen(nextEv)}` : 'Agenda'}
        </Typography>
      </Box>

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 340, maxHeight: 440, p: 1.5, overflow: 'auto' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
            }}
          >
            Agenda do ticket
          </Typography>
          {canEditTicket && (
            <Button size="small" startIcon={<AddIcon />} onClick={openNew} variant="outlined">
              Novo
            </Button>
          )}
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : events.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            Nenhum compromisso neste período. Inclua eventos públicos dos envolvidos e os vinculados a este ticket.
          </Typography>
        ) : (
          <List dense disablePadding>
            {events.map((ev) => (
              <ListItem
                key={ev.id}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                  cursor: 'pointer',
                }}
                onClick={() => (isMine(ev) ? openEdit(ev) : openView(ev))}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" component="span">
                        {ev.title}
                      </Typography>
                      {ev.source === 'ticket_sla' && (
                        <Chip size="small" color="secondary" variant="outlined" label="SLA" />
                      )}
                      <Chip size="small" label={ev.visibility === 'public' ? 'Público' : 'Privado'} />
                      {ev.owner?.name && (
                        <Chip size="small" variant="outlined" label={ev.owner.name} />
                      )}
                    </Box>
                  }
                  secondary={formatEventRange(ev.starts_at, ev.ends_at, ev.all_day)}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Popover>

      <CalendarEventDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
          setDialogReadOnly(false);
        }}
        onSaved={load}
        event={editing}
        users={users}
        currentUserId={currentUser?.id}
        defaultTicketId={
          editing
            ? Number(editing.ticket_id) === Number(ticketId)
              ? ticketId
              : null
            : ticketId
        }
        defaultTicketNumber={
          editing
            ? Number(editing.ticket_id) === Number(ticketId)
              ? ticketNumber
              : null
            : ticketNumber
        }
        readOnly={dialogReadOnly}
      />
    </>
  );
};

export default TicketAgendaPanel;
